import { flaggingCallback, useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  DatabaseEntry,
  DatabaseFieldType,
  RawDatabaseValue,
} from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isNonEmptyEntryLinkValue } from '@databases/utils/database/field-types/entryLink';
import { getDatabaseEntryOptional, getEntryName } from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus,
} from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { EditableListProps } from '../../EditableList';
import { EditableListWithPlaceholder } from '../../EditableListWithPlaceholder';
import { useScope } from '../../Scope';
import EntryPicker from '../../database-entry/EntryPicker';
import { EditComponentProps } from '../fieldTypes';

type Value = {
  entryRef: NonNullable<RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>>[0];
  entry?: DatabaseEntry;
};

const getId = (value: Value): string => value.entryRef._id;

const getNewValue = (): Value => ({
  entryRef: { _id: generateId() },
});

const EntryLinkValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.ENTRY_LINK>
> = ({ onChange, onFocus, onBlur, autoFocus, value, field }) => {
  const {
    databaseSpace,
    databaseId,
    multi,
    contentId: databaseContentId,
  } = field.settings;
  const scope = useScope();
  const intl = useIntl();
  const { formatMessage } = intl;
  const [isPending, targetDatabaseDraft] = defaultStoreUtils.useShallowEqualSelector(
    (state) => {
      if (databaseContentId) {
        const targetDatabaseStatus = selectDatabaseByContentIdStatus(state, {
          contentId: databaseContentId,
        });
        const targetDatabase = selectDatabaseByContentIdOptional(state, {
          contentId: databaseContentId,
        });
        return [
          targetDatabaseStatus?.status === AsyncOperationStatus.PENDING,
          targetDatabase &&
            selectDatabaseDraftByContentId(state, {
              contentId: databaseContentId,
              scope,
              includeDeleted: false,
            }),
        ] as const;
      } else {
        return [false, undefined];
      }
    }
  );

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useFlaggingCallback(async () => {
      if (databaseContentId) {
        await dispatch(loadDatabaseByContentId(databaseContentId, intl));
      }
    }, [databaseContentId, dispatch, intl])
  );

  const resolved = useMemo(() => {
    let result: Value[];
    if (!targetDatabaseDraft) {
      result = [];
    } else {
      result = value.flatMap((entryRef) => {
        if (isNonEmptyEntryLinkValue(entryRef)) {
          const entry = getDatabaseEntryOptional(targetDatabaseDraft, entryRef.entryId);
          return entry ? [{ entryRef, entry }] : [{ entryRef }];
        } else {
          return [{ entryRef }];
        }
      });
    }

    return result;
  }, [targetDatabaseDraft, value]);

  const store = defaultStoreUtils.useStore();

  const handleSingleChange = useFlaggingCallback(
    async (val: string | undefined) => {
      if (!val || !targetDatabaseDraft) {
        if (resolved.length > 0) {
          await onChange([], {
            type: DatabaseFieldType.ENTRY_LINK,
            delta: {
              type: MultiValueChangeType.REMOVE,
              index: 0,
            },
          });
        }
      } else {
        const entry = targetDatabaseDraft.entriesById[val];
        const entryName = getEntryName(
          { database: targetDatabaseDraft, entry },
          store.getState()
        );
        const entryLinkValue = {
          _id: value[0]?._id ?? generateId(),
          entryId: val,
          title: entryName
            ? entryName
            : formatMessage(messages['unnamed-entities.unnamed-entry']),
        };

        await onChange([entryLinkValue], {
          type: DatabaseFieldType.ENTRY_LINK,
          delta:
            resolved.length > 0
              ? {
                  type: MultiValueChangeType.EDIT,
                  index: 0,
                  value: entryLinkValue,
                }
              : {
                  type: MultiValueChangeType.APPEND,
                  value: entryLinkValue,
                },
        });
      }
    },
    [targetDatabaseDraft, resolved.length, onChange, store, value, formatMessage]
  );

  const onItemChange = useFlaggingCallback(
    async (val: Value, newValueIndex: number, _: null, allValues: Value[]) => {
      await onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.EDIT,
            value: val.entryRef,
            index: newValueIndex,
          },
        }
      );
    },
    [onChange]
  );

  const onItemAdd = useFlaggingCallback(
    async (val: Value, allValues: Value[]) => {
      await onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.APPEND,
            value: val.entryRef,
          },
        }
      );
    },
    [onChange]
  );

  const onItemDelete = useFlaggingCallback(
    async (_: Value, removedIndex: number, allValues: Value[]) => {
      await onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.REMOVE,
            index: removedIndex,
          },
        }
      );
    },
    [onChange]
  );

  const onItemReorder = useFlaggingCallback(
    async (_: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      await onChange(
        allValues.map((v) => v.entryRef),
        {
          type: DatabaseFieldType.ENTRY_LINK,
          delta: {
            type: MultiValueChangeType.REORDER,
            currentIndex,
            newIndex,
          },
        }
      );
    },
    [onChange]
  );

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, autoFocus, onChange }) => (
      <EntryPicker
        database={targetDatabaseDraft}
        isLoading={isPending}
        isDisabled={isPending}
        noOptionsMessage={
          !databaseSpace || !databaseId
            ? formatMessage(messages['entry-link.no-options'])
            : ''
        }
        value={'entryId' in value.entryRef ? value.entryRef.entryId : ''}
        onChange={flaggingCallback((val) => {
          if (val && targetDatabaseDraft) {
            const entry = targetDatabaseDraft.entriesById[val];
            const entryName = getEntryName(
              { database: targetDatabaseDraft, entry },
              store.getState()
            );
            onChange(
              {
                entryRef: {
                  _id: value.entryRef._id,
                  entryId: val,
                  title: entryName
                    ? entryName
                    : formatMessage(messages['unnamed-entities.unnamed-entry']),
                },
                entry,
              },
              null
            );
          } else {
            onChange(
              {
                entryRef: { _id: value.entryRef._id },
              },
              null
            );
          }
        })}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isSeamless
        isLinkPreview
      />
    ),
    [
      targetDatabaseDraft,
      isPending,
      databaseSpace,
      databaseId,
      formatMessage,
      onFocus,
      onBlur,
      store,
    ]
  );

  if (multi || resolved.length > 1) {
    return (
      <EditableListWithPlaceholder<Value, null>
        getId={getId}
        values={resolved}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={value.length === 0 ? autoFocus : undefined}
        addLabel="Add entry"
        noAdd={!multi}
      />
    );
  } else {
    return (
      <EntryPicker
        database={targetDatabaseDraft}
        isLoading={isPending}
        isDisabled={isPending}
        noOptionsMessage={
          !databaseSpace || !databaseId
            ? formatMessage(messages['entry-link.no-options'])
            : ''
        }
        value={
          resolved.length && 'entryId' in resolved[0].entryRef
            ? resolved[0].entryRef.entryId
            : undefined
        }
        onChange={handleSingleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isClearable
        isLinkPreview
      />
    );
  }
};

export default EntryLinkValueEdit;
